/** Minimal case shape the windowing logic needs. */
type ScheduledCase = {
  scheduledDateTime: Date;
  durationMinutes: number | null;
};

const getCaseEndTime = (caseItem: ScheduledCase) => {
  if (!caseItem.durationMinutes || caseItem.durationMinutes <= 0) {
    return caseItem.scheduledDateTime;
  }

  return new Date(
    caseItem.scheduledDateTime.getTime() + caseItem.durationMinutes * 60 * 1000
  );
};

const getWindowSlice = <T extends ScheduledCase>(
  cases: T[],
  anchorIndex: number
): T[] => {
  if (anchorIndex <= 0) {
    return cases.slice(0, 3);
  }

  if (anchorIndex >= cases.length - 1) {
    return cases.slice(-3);
  }

  return cases.slice(anchorIndex - 1, anchorIndex + 2);
};

/**
 * Returns the three cases most relevant to "now": the active case plus its
 * neighbours, or the nearest three at the start/end of the day.
 */
export const getCaseWindow = <T extends ScheduledCase>(cases: T[]): T[] => {
  if (cases.length <= 3) return cases;

  const now = new Date();
  const activeIndex = cases.findIndex((caseItem) => {
    const currentTime = now.getTime();
    const startTime = caseItem.scheduledDateTime.getTime();
    const endTime = getCaseEndTime(caseItem).getTime();

    return currentTime >= startTime && currentTime < endTime;
  });

  if (activeIndex !== -1) {
    return getWindowSlice(cases, activeIndex);
  }

  if (now < cases[0].scheduledDateTime) {
    return cases.slice(0, 3);
  }

  const lastCaseEndTime = getCaseEndTime(cases[cases.length - 1]);
  if (now >= lastCaseEndTime) {
    return cases.slice(-3);
  }

  const nextCaseIndex = cases.findIndex(
    (caseItem) => caseItem.scheduledDateTime > now
  );
  if (nextCaseIndex === -1) {
    return cases.slice(-3);
  }

  const previousCaseIndex = nextCaseIndex - 1;
  if (previousCaseIndex <= 0) {
    return cases.slice(0, 3);
  }

  return getWindowSlice(cases, previousCaseIndex);
};
